import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { ResetPasswordDraft } from '../domain/reset-password.model';
import { validateDraft } from '../domain/reset-password.validation';
import { toResetPasswordError } from '../domain/reset-password-error';
import type { ResetPasswordGateway } from '../infrastructure/reset-password.gateway';
import { submitRequested } from './reset-password.actions';
import { selectDraft } from './reset-password.selectors';
import { resetPasswordActions } from './reset-password.slice';

/**
 * WORKER: reset the password with the drafted pair and the one-time token
 * carried by the action (read from the URL by the page; never stored).
 * Validates up front (a `validation` error short-circuits before any I/O), then
 * all I/O goes through the injected gateway (read from the saga `context`;
 * never the api client), so tests drive the success / api-error / network-error
 * branches with an in-memory gateway. `takeLatest` means a fresh submit
 * supersedes any in-flight request.
 */
function* submitWorker(action: ReturnType<typeof submitRequested>) {
  const draft = (yield select(selectDraft)) as ResetPasswordDraft;

  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(resetPasswordActions.submitFailed({ kind: 'validation', fields: errors }));
    return;
  }

  yield put(resetPasswordActions.submitStarted());
  const gateway: ResetPasswordGateway = yield getContext('resetPasswordGateway');
  try {
    yield call(gateway.resetPassword, {
      token: action.payload.token,
      password: draft.password,
    });
    yield put(resetPasswordActions.submitSucceeded());
  } catch (e) {
    yield put(resetPasswordActions.submitFailed(toResetPasswordError(e)));
  }
}

/** WATCHERS. */
export function* resetPasswordSaga() {
  yield all([takeLatest(submitRequested.type, submitWorker)]);
}
